import type { ShortcutKey } from "@/actions/keybinding";
import type { TActionWithOptionalArgs } from "./types";

export type TActionCategory =
	| "playback"
	| "navigation"
	| "editing"
	| "selection"
	| "history"
	| "timeline"
	| "controls"
	| "assets";

export interface TActionBaseDefinition {
	description: string;
	category: TActionCategory;
	args?: Record<string, unknown>;
}

export interface TActionDefinition extends TActionBaseDefinition {
	defaultShortcuts?: readonly ShortcutKey[];
}

export const ACTIONS = {
	"toggle-play": {
		description: "Воспроизвести/Пауза",
		category: "playback",
	},
	"stop-playback": {
		description: "Остановить воспроизведение",
		category: "playback",
	},
	"seek-forward": {
		description: "Перемотка вперёд на 1 секунду",
		category: "playback",
		args: { seconds: "number" },
	},
	"seek-backward": {
		description: "Перемотка назад на 1 секунду",
		category: "playback",
		args: { seconds: "number" },
	},
	"frame-step-forward": {
		description: "Следующий кадр",
		category: "navigation",
	},
	"frame-step-backward": {
		description: "Предыдущий кадр",
		category: "navigation",
	},
	"jump-forward": {
		description: "Переход вперёд на 5 секунд",
		category: "navigation",
		args: { seconds: "number" },
	},
	"jump-backward": {
		description: "Переход назад на 5 секунд",
		category: "navigation",
		args: { seconds: "number" },
	},
	"goto-start": {
		description: "Перейти к началу таймлайна",
		category: "navigation",
	},
	"goto-end": {
		description: "Перейти к концу таймлайна",
		category: "navigation",
	},
	split: {
		description: "Разделить элементы по позиции плейхеда",
		category: "editing",
	},
	"split-left": {
		description: "Разделить и удалить левую часть",
		category: "editing",
	},
	"split-right": {
		description: "Разделить и удалить правую часть",
		category: "editing",
	},
	"delete-selected": {
		description: "Удалить текущее выделение",
		category: "editing",
	},
	"copy-selected": {
		description: "Копировать выделенные элементы",
		category: "editing",
	},
	"paste-copied": {
		description: "Вставить элементы по позиции плейхеда",
		category: "editing",
	},
	"toggle-snapping": {
		description: "Переключить прилипание",
		category: "editing",
	},
	"toggle-ripple-right": {
		description: "Переключить волновое редактирование (вправо)",
		category: "editing",
	},
	"toggle-ripple-left": {
		description: "Переключить волновое редактирование (влево)",
		category: "editing",
	},
	"toggle-source-audio": {
		description: "Извлечь или восстановить исходный звук",
		category: "editing",
	},
	"select-all": {
		description: "Выделить все элементы",
		category: "selection",
	},
	"cancel-interaction": {
		description: "Отменить текущее действие",
		category: "controls",
	},
	"deselect-all": {
		description: "Снять выделение со всех элементов",
		category: "selection",
	},
	"duplicate-selected": {
		description: "Дублировать выделенный элемент",
		category: "selection",
	},
	"toggle-elements-muted-selected": {
		description: "Включить/выключить звук выделенных элементов",
		category: "selection",
	},
	"toggle-elements-visibility-selected": {
		description: "Показать/скрыть выделенные элементы",
		category: "selection",
	},
	"toggle-bookmark": {
		description: "Переключить закладку по позиции плейхеда",
		category: "timeline",
	},
	undo: {
		description: "Отменить",
		category: "history",
	},
	redo: {
		description: "Повторить",
		category: "history",
	},
	"remove-media-asset": {
		description: "Удалить медиафайл",
		category: "assets",
		args: { projectId: "string", assetId: "string" },
	},
	"remove-media-assets": {
		description: "Удалить медиафайлы",
		category: "assets",
		args: { projectId: "string", assetIds: "string[]" },
	},
} as const satisfies Record<string, TActionBaseDefinition>;

export type TAction = keyof typeof ACTIONS;

const ACTION_DEFAULT_SHORTCUTS = [
	["toggle-play", ["space", "k"]],
	["seek-forward", ["l"]],
	["seek-backward", ["j"]],
	["frame-step-forward", ["right"]],
	["frame-step-backward", ["left"]],
	["jump-forward", ["shift+right"]],
	["jump-backward", ["shift+left"]],
	["goto-start", ["home", "enter"]],
	["goto-end", ["end"]],
	["split", ["s"]],
	["split-left", ["q"]],
	["split-right", ["w"]],
	["delete-selected", ["backspace", "delete"]],
	["copy-selected", ["ctrl+c"]],
	["paste-copied", ["ctrl+v"]],
	["toggle-snapping", ["n"]],
	["select-all", ["ctrl+a"]],
	["cancel-interaction", ["escape"]],
	["duplicate-selected", ["ctrl+d"]],
	["undo", ["ctrl+z"]],
	["redo", ["ctrl+shift+z", "ctrl+y"]],
] as const satisfies ReadonlyArray<
	readonly [TActionWithOptionalArgs, readonly ShortcutKey[]]
>;

const ACTION_DEFAULT_SHORTCUTS_BY_ACTION = new Map<
	TAction,
	readonly ShortcutKey[]
>(ACTION_DEFAULT_SHORTCUTS);

export function getActionDefinition({
	action,
}: {
	action: TAction;
}): TActionDefinition {
	return {
		...ACTIONS[action],
		defaultShortcuts: ACTION_DEFAULT_SHORTCUTS_BY_ACTION.get(action),
	};
}

export function getDefaultShortcuts(): Map<
	ShortcutKey,
	TActionWithOptionalArgs
> {
	const shortcuts = new Map<ShortcutKey, TActionWithOptionalArgs>();

	for (const [action, defaultShortcuts] of ACTION_DEFAULT_SHORTCUTS) {
		for (const shortcut of defaultShortcuts) {
			shortcuts.set(shortcut, action);
		}
	}

	return shortcuts;
}
